import * as React from 'react';

import type {
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
  DescriptionPage_CourseType,
} from '__generated__/graphql-types';
import { branch, compose, withProps } from 'recompose';

import user from 'js/lib/user';

import type { EventData } from '@coursera/event-pulse/react';

import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import useEnrollButtonData from 'bundles/enroll-common/components/xdp/useEnrollButtonData';
import type { UseEnrollButtonDataResult } from 'bundles/enroll-common/components/xdp/useEnrollButtonData';
import useEnrollButtonDerivedProps from 'bundles/enroll-common/components/xdp/useEnrollButtonDerivedProps';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { withPrice } from 'bundles/enroll-common/components/xdp/withNaptimeData';
import withEnrollModal from 'bundles/enroll-modal/components/withEnrollModal';
import type { Course } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';
import BannerEnroll from 'bundles/enterprise-legacy-xdp/components/conversion/BannerEnroll';
import {
  withFinaidByUserAndProducts,
  withSpecializations,
} from 'bundles/finaid-common/enhancers/withCoursesAndFinancialAidApplications';
import { withFinancialAidApplicationsByUserAndProduct } from 'bundles/finaid-common/enhancers/withFinancialAidApplications';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';

const WithEnrollModal = withEnrollModal(BannerEnroll);

type PropsFromCaller = {
  onlyShowButton?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  accentColor?: string;
  isSpecialization: boolean;
  courseCount?: number;
  courseId?: string;
  s12nId?: string;
  partnerIds?: string[];
  s12nProductVariant?: S12nProductVariant | DescriptionPageProductVariant;
  s12nCourses?: Course[];
  name?: string;
  slug?: string;
  buttonMinHeightInPixels?: number;
  buttonStyle?: React.CSSProperties;
  enrollBtnCustomStyles?: React.CSSProperties;
  course?: Course;
  onStickyHeader?: boolean;
  buttonTrackingData?: Omit<EventData<'click_button'>, 'button'>;
  wrapperClassName?: React.CSSProperties; // used in BannerEnroll
  buttonRef?: React.RefCallback<HTMLButtonElement>;
  courseType?: DescriptionPage_CourseType;
  // Forwarded to withEnrollModal: return `true` to skip the modal (e.g. go straight to checkout).
  alternateEnrollFlow?: (enrollmentAvailableChoices?: EnrollmentChoices) => boolean;
  // Forces the enroll button into a loading state (e.g. while an alternateEnrollFlow checkout is in flight).
  isCheckoutLoading?: boolean;
};

type PropsToComponent = PropsFromCourseraPlusProductOwnerships & EligibilityCheckProps;

// Thin wrapper component that gathers all the data the enroll button needs by calling
// `useEnrollButtonData` (queries, userAgent, router, enrollment choices, and Coursera Plus product
// ownerships), then feeds the results into the remaining HOC chain as props. See the JSDoc on
// `useEnrollButtonData` for the list of HOC layers it replaces.
const withEnrollButtonHooks =
  (withEnrollmentNotRequired?: boolean) =>
  <InputProps extends PropsFromCaller>(Component: React.ComponentType<InputProps & UseEnrollButtonDataResult>) => {
    const WithEnrollButtonHooks = (props: InputProps) => {
      const enrollButtonData = useEnrollButtonData(
        {
          courseId: props.courseId,
          s12nId: props.s12nId,
          isSpecialization: props.isSpecialization,
          partnerIds: props.partnerIds,
        },
        { withEnrollmentNotRequired }
      );

      return <Component {...props} {...enrollButtonData} />;
    };

    return WithEnrollButtonHooks;
  };

// The remaining HOC layers that have no hook equivalent yet: price (ownable products), promotions,
// finaid, and the Coursera Plus product eligibility check. They consume props injected by
// `withEnrollButtonHooks` (e.g. `enrollmentAvailableChoices`, `course`, `s12n`, `router`), so they
// stay between the data hooks and the derivation. All happen on the client and should be
// non-blocking; they are gated on an authenticated user. Tracked for hook migration under Major 3.
const withExternalEnrollData = branch(
  () => typeof window !== 'undefined' && user.isAuthenticatedUser(),
  compose(
    withPrice(),
    withPromotionInfo(true),
    branch<PropsFromCaller>(
      (props) => Boolean(props.isSpecialization && props.s12nId),
      compose(withSpecializations(true), withFinaidByUserAndProducts(true)),
      withFinancialAidApplicationsByUserAndProduct(undefined, true)
    ),
    branch<PropsFromCaller>(
      ({ isCourseraPlusEligibleProduct }) => !isCourseraPlusEligibleProduct,
      withCourseraPlusProductEligibilityCheck(true)
    )
  )
);

export const withEnrollButtonData = <InputProps extends PropsFromCaller>(withEnrollmentNotRequired?: boolean) =>
  compose<PropsToComponent, InputProps>(
    // 1) Gather data via hooks (see `useEnrollButtonData`) and inject it as props.
    withEnrollButtonHooks(withEnrollmentNotRequired),
    // 2) Layer the remaining HOC-sourced data (price, promotions, finaid, C+ eligibility) on top.
    withExternalEnrollData,
    // 3) Derive the final presentational props from everything above.
    withProps(useEnrollButtonDerivedProps)
  );

// @ts-expect-error TSMIGRATION
export default withEnrollButtonData(true)(WithEnrollModal);
